import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { get } from "../../lib/api";
import { isOrderLive, moneyFlow } from "../../lib/orderFlow";
import { energyKwh, orderMoney, orderOutcome, orderStateTone } from "../../lib/publicOrder";
import type { PublicOrderDetail } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Freshness } from "../ui/Freshness";
import { Modal } from "../ui/Modal";
import { Pill, StatusMark } from "../ui/Status";
import { Metric, MicroLabel } from "../ui/Text";
import { MoneyFlowBar } from "./MoneyFlowBar";
import { OrderJourney } from "./OrderJourney";
import { OrderParty } from "./OrderParty";
import { OrderRecord } from "./OrderRecord";

/**
 * One public charge order, over the list it was clicked from. pay-ui spec §9.5.
 *
 * Arranged around the question an operator opens an order with, which is almost
 * always about money. In falling order of weight:
 *
 * 1. Somebody's money is being held — said first, in words, when it is true.
 * 2. Where the money went — what was taken, and one bar dividing it into what was
 *    used, returned, owed, kept or released. The reader no longer subtracts.
 * 3. How far the order got — four steps, created to ended.
 * 4. The record — payment attempts and the order's own fields, folded away.
 *
 * Where it charged, the price and the settlement reference sit to the side.
 * The outcome sentence is the title: it is the order's verdict, in words.
 *
 * Polled only while the order is still moving on its own (`isOrderLive`).
 */

const POLL_MS = 10_000;

export function OrderView({
  orderId,
  open,
  onOpenChange,
}: {
  readonly orderId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  // Held past a close, so the panel keeps its order while it animates out.
  const [shownId, setShownId] = useState(orderId);
  if (orderId !== "" && orderId !== shownId) setShownId(orderId);

  const order = useQuery({
    queryKey: ["public-order", shownId],
    queryFn: () => get<PublicOrderDetail>(`/public-orders/${encodeURIComponent(shownId)}`),
    enabled: open && shownId !== "",
    refetchInterval: (query) =>
      query.state.data !== undefined && isOrderLive(query.state.data.state) ? POLL_MS : false,
  });
  const row = order.data;

  return (
    <Modal
      size="wide"
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={row === undefined ? "Public charging" : `Public charging · ${row.depotName}`}
      title={row === undefined ? "Order" : orderOutcome(row)}
      status={
        row === undefined ? undefined : (
          <>
            <Pill tone={orderStateTone(row.state)}>{row.state}</Pill>
            {isOrderLive(row.state) ? (
              <Freshness
                updatedAt={order.dataUpdatedAt}
                isFetching={order.isFetching}
                intervalMs={POLL_MS}
              />
            ) : null}
          </>
        )
      }
      footer={
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      }
    >
      {order.isError ? (
        <ErrorNotice error={order.error} onRetry={() => void order.refetch()} />
      ) : row === undefined ? (
        <span role="status">
          <MicroLabel>Loading</MicroLabel>
        </span>
      ) : (
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-8 lg:col-span-2">
            <Debt row={row} />
            <Money row={row} />
            <OrderJourney row={row} />
            <div className="flex flex-col">
              <OrderRecord row={row} />
            </div>
          </div>
          <OrderParty row={row} />
        </div>
      )}
    </Modal>
  );
}

/** Tier 1: only when money is owed back, and in words. */
function Debt({ row }: { readonly row: PublicOrderDetail }) {
  if (row.refundOwedMinor === "0") return null;

  const figures = orderMoney(row);
  const needsHands = row.state === "refund_failed";

  return (
    <section
      aria-label="Money owed"
      className="flex flex-col gap-2 border-l-2 border-signal bg-signal-wash/40 px-4 py-3"
    >
      <p className="m-0 flex items-start gap-2 text-body text-ink">
        <span className="pt-1.5">
          <StatusMark tone="faulted" />
        </span>
        <span>
          <strong className="font-semibold">{figures.owed}</strong> has not been returned
          {row.refundReason === null ? "" : ` (${row.refundReason})`}.{" "}
          {needsHands
            ? "Automatic refunding has given up. Refund it in the merchant portal against the settlement reference; the next sweep picks a hand refund up on its own."
            : "The refund worker retries it on every sweep — no action needed unless it reaches refund failed."}
        </span>
      </p>
    </section>
  );
}

/**
 * Tier 2. The amount taken is the figure set large, with the one beside it that
 * changes what the operator does: what is owed back when anything is, and the
 * energy delivered when nothing is. The bar beneath divides the first.
 */
function Money({ row }: { readonly row: PublicOrderDetail }) {
  const flow = moneyFlow(row);
  const figures = orderMoney(row);
  const owes = row.refundOwedMinor !== "0";

  return (
    <section aria-label="Money" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start gap-x-12 gap-y-5">
        <Metric
          label={flow?.totalLabel ?? (row.paymentFlow === "pre_auth" ? "Held" : "Paid")}
          value={flow?.total ?? "—"}
          hint={takenHint(row, figures.taken, flow === null)}
        />
        {owes ? (
          <Metric label="Owed back" value={figures.owed} tone="signal" hint="not yet returned" />
        ) : (
          <Metric
            label="Delivered"
            value={energyKwh(row.deliveredWh)}
            // Spread rather than `undefined`: a unit beside an em dash reads "— kWh".
            {...(row.deliveredWh === null ? {} : { unit: "kWh" })}
            // Zero is a true figure, but not the one to shout: the bar and the title
            // already say nothing was charged.
            tone={row.deliveredWh === null || Number(row.deliveredWh) === 0 ? "muted" : "ink"}
            hint={row.deliveredWh === null ? "no committed figure" : "committed at close"}
          />
        )}
      </div>
      {flow === null ? null : <MoneyFlowBar flow={flow} deliveredWh={row.deliveredWh} />}
    </section>
  );
}

/**
 * Under a hold, the authorised amount is not money taken, and the caption says
 * what was. Under a purchase, whether it settled at all.
 */
function takenHint(row: PublicOrderDetail, taken: string, isUnpaid: boolean): string {
  if (isUnpaid) return "nothing was taken";
  if (row.paymentFlow === "pre_auth") {
    if (taken !== "—") return `${taken} taken from it`;
    if (row.state === "released") return "released, nothing taken";
    if (row.state === "capture_failed") return "never collected";
    return "on the card, nothing taken yet";
  }
  return "taken from the driver";
}
